import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { X, User, GraduationCap, Wrench, Trash2, FileText } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import SupportRequestForm from "@/components/SupportRequestForm";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { checkFieldQuality } from "@/lib/gibberishFilter";
import { matchSchoolName } from "@/lib/schoolNames";
import { useNavigate, Link } from "react-router-dom";
import ModerationQueue from "@/components/ModerationQueue";
import PioneerBadge from "@/components/PioneerBadge";
import { usePioneerStatus } from "@/hooks/usePioneerStatus";

const ProfilePage = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { isPioneer, count: pioneerCount } = usePioneerStatus(user?.id);
  const navigate = useNavigate();
  const [school, setSchool] = useState("");
  const [major, setMajor] = useState("");
  const [classYear, setClassYear] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [myReviews, setMyReviews] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;
    const fetchMyReviews = async () => {
      const { data } = await supabase
        .from("reviews_public" as any)
        .select("id, title, organization, type, rating, major, created_at")
        .eq("is_owner", true)
        .order("created_at", { ascending: false });
      setMyReviews((data as any[]) || []);
    };
    fetchMyReviews();
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles" as any)
        .select("school, major, skills, class_year")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        const d = data as any;
        setSchool(d.school || "");
        setMajor(d.major || "");
        setClassYear(d.class_year ? String(d.class_year) : "");
        setSkills(d.skills || []);
      }
      setLoaded(true);
    };
    fetchProfile();
  }, [user]);

  const addSkill = () => {
    const trimmed = skillInput.trim();
    if (trimmed && !skills.includes(trimmed) && skills.length < 10) {
      setSkills([...skills, trimmed]);
      setSkillInput("");
    }
  };

  const removeSkill = (skill: string) => {
    setSkills(skills.filter((s) => s !== skill));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    for (const [val, label] of [[school, "School"], [major, "Major"]] as const) {
      const fq = checkFieldQuality(val, label);
      if (!fq.ok) {
        toast({ title: "Please improve your profile", description: fq.reason, variant: "destructive" });
        return;
      }
    }
    for (const s of skills) {
      const fq = checkFieldQuality(s, "Skill");
      if (!fq.ok) {
        toast({ title: "Please improve your skills", description: fq.reason, variant: "destructive" });
        return;
      }
    }

    setSaving(true);

    // Normalize the school name and merge it into an existing school when the
    // names are confidently the same (casing, abbreviations, typos). Anything
    // ambiguous stays its own school.
    const [{ data: statRows }, { data: baseRows }] = await Promise.all([
      supabase.from("school_stats" as any).select("school"),
      supabase.from("school_baselines" as any).select("school"),
    ]);
    const known = Array.from(
      new Set([
        ...(((statRows as any[]) ?? []).map((r) => r.school as string)),
        ...(((baseRows as any[]) ?? []).map((r) => r.school as string)),
      ].filter(Boolean))
    );
    const { name: normalizedSchool, merged } = matchSchoolName(school, known);

    const { error } = await supabase
      .from("profiles" as any)
      .update({ school: normalizedSchool, major, skills, class_year: classYear ? Number(classYear) : null, updated_at: new Date().toISOString() } as any)
      .eq("user_id", user.id);

    setSaving(false);
    if (error) {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
      return;
    }
    if (normalizedSchool !== school) setSchool(normalizedSchool);
    toast({
      title: "Profile updated!",
      description:
        merged && normalizedSchool.toLowerCase() !== school.trim().toLowerCase()
          ? `School saved as "${normalizedSchool}" to match existing records.`
          : undefined,
    });
  };


  if (authLoading || !loaded) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container max-w-md py-20 text-center text-muted-foreground">Loading...</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Header */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-transparent" />
        <div className="container relative py-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-2xl text-foreground">Your Profile</h1>
                {isPioneer && <PioneerBadge />}
              </div>
              <p className="text-sm text-muted-foreground">
                {isPioneer
                  ? `You're a Pioneer — first to document ${pioneerCount} experience${pioneerCount === 1 ? "" : "s"} on Vecurate.`
                  : "This info is for your profile only — all reviews remain anonymous."}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <main className="container max-w-lg py-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-border bg-card p-6 shadow-card"
        >
          <form onSubmit={handleSave} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="school" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-muted-foreground" />
                School
              </Label>
              <Input id="school" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="e.g. University of Michigan" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="major" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-muted-foreground" />
                Major
              </Label>
              <Input id="major" value={major} onChange={(e) => setMajor(e.target.value)} placeholder="e.g. Pre-Med Biology" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="class-year" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-muted-foreground" />
                Class year
              </Label>
              <Input
                id="class-year"
                type="number"
                min={1950}
                max={2100}
                value={classYear}
                onChange={(e) => setClassYear(e.target.value)}
                placeholder="e.g. 2027"
              />
              <p className="text-xs text-muted-foreground">
                Your school and class year power the{" "}
                <Link to="/leaderboard" className="text-primary hover:underline">School Challenge leaderboard</Link>.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-muted-foreground" />
                Skills (up to 10)
              </Label>
              <div className="flex gap-2">
                <Input
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="e.g. Patient Care"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                />
                <Button type="button" variant="outline" onClick={addSkill} className="rounded-lg">Add</Button>
              </div>
              {skills.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {skills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="gap-1 text-xs rounded-full px-3">
                      {skill}
                      <button type="button" onClick={() => removeSkill(skill)}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" disabled={saving}>
              {saving ? "Saving..." : "Save Profile"}
            </Button>
          </form>
        </motion.div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-serif text-lg text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              Your reviews ({myReviews.length})
            </h2>
            <Link to="/activity" className="text-xs text-primary hover:underline">
              All activity
            </Link>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Click a review to open it. Your reviews stay anonymous to other users.
          </p>
          {myReviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">You haven't posted any reviews yet.</p>
          ) : (
            <div className="space-y-2">
              {myReviews.map((r) => (
                <Link
                  key={r.id}
                  to={`/review/${r.id}`}
                  className="block rounded-lg border border-border p-3 transition-colors hover:border-primary/50 hover:bg-muted/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-serif text-sm text-card-foreground truncate">
                        {r.major && r.major.trim() ? r.major : r.title}
                      </h3>
                      <p className="text-xs text-muted-foreground truncate">{r.organization}</p>
                    </div>
                    <span className="text-xs font-bold text-primary shrink-0">{Number(r.rating).toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
                    <span>{r.type}</span>
                    <span>•</span>
                    <span>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 rounded-2xl border border-destructive/30 bg-card p-6 shadow-card">
          <h2 className="font-serif text-lg text-foreground mb-1">Delete your account</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Account deletion is handled by our team to keep submitted reviews anonymous. Submit a request and we'll process it within 7 days.
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-lg gap-2 text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4" />
                Request account deletion
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Request account deletion</DialogTitle>
                <DialogDescription>
                  We'll confirm by email before removing your account. Reviews stay anonymous either way.
                </DialogDescription>
              </DialogHeader>
              <SupportRequestForm
                requestType="account_deletion"
                defaultSubject="Account deletion request"
                submitLabel="Submit deletion request"
              />
            </DialogContent>
          </Dialog>
        </div>
        <ModerationQueue isAdmin={!!isAdmin} />
      </main>
    </div>
  );
};

export default ProfilePage;
